import type {
    FileActor,
    FilePolicy,
    FilePolicyAction,
    FilePolicyGrant,
    FilePolicyPrincipal,
    StoredFilePolicy,
} from './types';

const FILE_POLICY_ACTIONS = new Set<FilePolicyAction>(['access', 'edit', 'preview', 'download']);
const READ_ACTIONS = new Set<FilePolicyAction>(['access', 'preview', 'download']);
type FilePolicyInput = FilePolicy | StoredFilePolicy | null | undefined;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
    return typeof value === 'string' && value.trim().length > 0;
}

function isFilePolicyAction(value: unknown): value is FilePolicyAction {
    return typeof value === 'string' && FILE_POLICY_ACTIONS.has(value as FilePolicyAction);
}

function isReadAction(action: FilePolicyAction): boolean {
    return READ_ACTIONS.has(action);
}

function normalizePrincipal(value: unknown): FilePolicyPrincipal | null {
    if (!isRecord(value) || typeof value.type !== 'string') {
        return null;
    }

    if (value.type === 'user' && isNonEmptyString(value.userId)) {
        return { type: 'user', userId: value.userId };
    }

    if (value.type === 'role' && isNonEmptyString(value.roleId)) {
        return { type: 'role', roleId: value.roleId };
    }

    if (value.type === 'project' && value.scope === 'any-member') {
        return { type: 'project', scope: 'any-member' };
    }

    return null;
}

function normalizeGrant(value: unknown): FilePolicyGrant | null {
    if (!isRecord(value) || !Array.isArray(value.actions) || value.actions.length === 0) {
        return null;
    }

    const principal = normalizePrincipal(value.principal);
    if (!principal || !value.actions.every(isFilePolicyAction)) {
        return null;
    }

    return {
        principal,
        actions: [...new Set(value.actions)],
    };
}

function grantMatchesActor(grant: FilePolicyGrant, actor: FileActor): boolean {
    switch (grant.principal.type) {
        case 'user':
            return grant.principal.userId === actor.userId;
        case 'role':
            return grant.principal.roleId === actor.roleId;
        case 'project':
            return grant.principal.scope === 'any-member' && isNonEmptyString(actor.userId);
    }
}

function grantAllowsAction(grant: FilePolicyGrant, action: FilePolicyAction): boolean {
    if (isReadAction(action)) {
        return grant.actions.some((grantAction) => isReadAction(grantAction));
    }

    return grant.actions.includes(action);
}

function hasProjectWideReadAccess(policy: FilePolicy): boolean {
    return policy.grants.some((grant) =>
        grant.principal.type === 'project' &&
        grant.principal.scope === 'any-member' &&
        grant.actions.some((action) => isReadAction(action))
    );
}

export function ownerOnlyPolicy(ownerUserId: string): FilePolicy {
    return {
        version: 1,
        ownerUserId,
        grants: [],
        defaultDeny: true,
    };
}

export function projectWideAccessPolicy(ownerUserId: string): FilePolicy {
    return {
        version: 1,
        ownerUserId,
        grants: [
            {
                principal: { type: 'project', scope: 'any-member' },
                actions: ['access'],
            },
        ],
        defaultDeny: true,
    };
}

export function normalizeFilePolicy(
    policy: FilePolicyInput,
    fallbackOwnerUserId: string
): FilePolicy {
    if (
        !isRecord(policy) ||
        policy.version !== 1 ||
        policy.defaultDeny !== true ||
        !isNonEmptyString(policy.ownerUserId) ||
        !Array.isArray(policy.grants)
    ) {
        return ownerOnlyPolicy(fallbackOwnerUserId);
    }

    return {
        version: 1,
        ownerUserId: policy.ownerUserId,
        grants: policy.grants.map(normalizeGrant).filter((grant): grant is FilePolicyGrant => grant !== null),
        defaultDeny: true,
    };
}

export function isFileAdmin(actor: FileActor): boolean {
    return actor.permissions.includes('MANAGE_FILE_ACCESS') ||
        actor.isProjectOwner === true ||
        actor.isOrganizationAdmin === true ||
        actor.isSuperAdmin === true;
}

export function evaluateFilePolicy(
    policy: FilePolicyInput,
    actor: FileActor,
    action: unknown,
    fallbackOwnerUserId: string
): boolean {
    if (!isNonEmptyString(actor.userId) || !isFilePolicyAction(action)) {
        return false;
    }

    const normalizedPolicy = normalizeFilePolicy(policy, fallbackOwnerUserId);
    if (actor.userId === normalizedPolicy.ownerUserId) {
        return true;
    }

    if (actor.isOrganizationAdmin === true) {
        return true;
    }

    if (action === 'edit' && isFileAdmin(actor)) {
        return true;
    }

    return normalizedPolicy.grants.some((grant) =>
        grantMatchesActor(grant, actor) && grantAllowsAction(grant, action)
    );
}

export function canManageFilePolicy(
    policy: FilePolicyInput,
    actor: FileActor,
    fallbackOwnerUserId: string
): boolean {
    if (!isNonEmptyString(actor.userId)) {
        return false;
    }

    const normalizedPolicy = normalizeFilePolicy(policy, fallbackOwnerUserId);
    return actor.userId === normalizedPolicy.ownerUserId || isFileAdmin(actor);
}

export function accessSummaryForPolicy(policy: FilePolicyInput): string {
    const fallbackOwnerUserId = isRecord(policy) && isNonEmptyString(policy.ownerUserId)
        ? policy.ownerUserId
        : '';
    const normalizedPolicy = normalizeFilePolicy(policy, fallbackOwnerUserId);

    if (hasProjectWideReadAccess(normalizedPolicy)) {
        return 'Anyone in project';
    }

    if (normalizedPolicy.grants.length === 0) {
        return 'Owner only';
    }

    return 'Restricted';
}
